/**
 * Filters the country list in the market switcher drawer as the customer
 * types. Matching is accent-insensitive and covers the whole label, so
 * "united", "uae", "eur" or "£" all work.
 *
 * - Enter submits the first visible country.
 * - Escape clears the filter first; a second Escape closes the drawer.
 * - The field is focused when the drawer opens on devices with a real
 *   pointer, and the filter is reset when the drawer closes.
 */
class MarketSwitcher extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['input', 'item']
  }

  CLASSES = {
    filtering: 'mkt-Switcher-filtering',
    empty: 'mkt-Switcher-empty',
  }

  connectedCallback () {
    super.connectedCallback()

    this.input = this.els.input.element
    this.items = this.els.item.elements.map((element) => ({
      element,
      text: this.normalise(element.textContent),
    }))

    this.drawer = this.closest('dialog')

    if (this.drawer) {
      this.observer = new MutationObserver(() => this.handleDrawerToggle())
      this.observer.observe(this.drawer, {
        attributes: true,
        attributeFilter: ['open'],
      })
    }
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    if (this.observer) this.observer.disconnect()
  }

  setupListeners () {
    super.setupListeners()

    const input = this.els.input.element

    this.listeners.add(input, 'input', () => this.filter())
    this.listeners.add(input, 'keydown', (event) => this.handleKeydown(event))

    if (this.els.clear) {
      this.listeners.add(this.els.clear.element, 'click', () => this.reset(true))
    }
  }

  handleDrawerToggle () {
    if (this.drawer.hasAttribute('open')) {
      // Don't pull up the keyboard on touch devices while the drawer animates,
      // and don't let the focus scroll the intro message out of view.
      if (window.matchMedia('(hover: hover)').matches) {
        this.input.focus({ preventScroll: true })
      }
    } else {
      this.reset()
    }
  }

  handleKeydown (event) {
    if (event.key === 'Enter') {
      event.preventDefault()

      const first = this.items.find(({ element }) => !element.hidden)

      if (first) first.element.click()
    }

    if (event.key === 'Escape' && this.input.value !== '') {
      event.stopPropagation()
      this.reset(true)
    }
  }

  filter () {
    const query = this.normalise(this.input.value)
    let visible = 0

    this.items.forEach(({ element, text }) => {
      const matches = query === '' || text.includes(query)

      element.hidden = !matches
      if (matches) visible += 1
    })

    this.classList.toggle(this.CLASSES.filtering, query !== '')
    this.classList.toggle(this.CLASSES.empty, visible === 0)
  }

  reset (refocus = false) {
    this.input.value = ''
    this.filter()

    if (refocus) this.input.focus()
  }

  normalise (value) {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  }
}

if (!customElements.get('market-switcher')) {
  customElements.define('market-switcher', MarketSwitcher)
}
